import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../../app/hooks";
import { AdminUser } from "../models/AdminUser";
import { changeUserLoginEmail } from "../slice-extensions/admin-slice-extension";

interface UseChangeLoginEmailModal {
  emailChangeUser: AdminUser | null;
  emailChangeBusy: boolean;
  openChangeLoginEmail: (user: AdminUser) => void;
  closeChangeLoginEmail: () => void;
  applyEmailChange: (newEmail: string, resetCredentials: boolean) => Promise<void>;
}

/**
 * Orchestrates the admin "change login email" modal: which user (if any) is
 * being handed over, the busy flag while the request is in flight, and the
 * dispatch + success/failure toast around applying the change.
 */
export function useChangeLoginEmailModal(): UseChangeLoginEmailModal {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [emailChangeUser, setEmailChangeUser] = useState<AdminUser | null>(null);
  const [emailChangeBusy, setEmailChangeBusy] = useState(false);

  const applyEmailChange = async (newEmail: string, resetCredentials: boolean) => {
    if (!emailChangeUser) return;
    setEmailChangeBusy(true);
    try {
      await dispatch(
        changeUserLoginEmail({
          instructorId: emailChangeUser.instructorId,
          request: { newEmail, resetCredentials },
        })
      ).unwrap();
      toast.success(t("admin.changeEmail.changed"));
      setEmailChangeUser(null);
    } catch (error) {
      toast.error((error as { message?: string })?.message || t("admin.changeEmail.failed"));
    } finally {
      setEmailChangeBusy(false);
    }
  };

  return {
    emailChangeUser,
    emailChangeBusy,
    openChangeLoginEmail: setEmailChangeUser,
    closeChangeLoginEmail: () => setEmailChangeUser(null),
    applyEmailChange,
  };
}
